import React, { useEffect, useRef, useState } from 'react';
import {
  BsArrowRepeat,
  BsCheckLg,
  BsClock,
  BsClipboard,
  BsLockFill,
  BsShieldFill,
  BsXCircle,
  BsXLg,
} from 'react-icons/bs';
import logoError from '../../assets/logo-error.png';
import logoPending from '../../assets/logo-pending.png';
import logoSuccess from '../../assets/logo-success.png';
import {
  checkoutDir,
  getCheckoutCopy,
  type CheckoutCopy,
  type CheckoutLocale,
} from '../checkout/checkoutI18n';
import './PaymentStatusCard.css';

export type PaymentStatusVariant = 'success' | 'error' | 'pending' | 'loading';

export function resolvePaymentStatusVariant(
  status: string,
): 'success' | 'error' | 'pending' {
  const normalized = status.trim().toUpperCase();
  if (['COMPLETED', 'SUCCESS', 'SUCCEEDED', 'APPROVED', 'CLEARED'].includes(normalized)) {
    return 'success';
  }
  if (['FAILED', 'DECLINED', 'REJECTED', 'CANCELLED', 'CANCELED', 'ERROR'].includes(normalized)) {
    return 'error';
  }
  return 'pending';
}

export type PaymentStatusDetail = {
  label: string;
  value: React.ReactNode;
};

export type PaymentStatusAction = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  autoTriggerAfterMs?: number;
  hint?: string;
};

export type PaymentRedirectConfig = {
  /** Countdown length in seconds before auto-redirect. */
  timerSeconds: number;
  /** URL to open when the countdown finishes. */
  redirectUrl: string;
};

type PaymentStatusCardProps = {
  variant: PaymentStatusVariant;
  title?: string;
  message?: React.ReactNode;
  transactionId?: string;
  amount?: string;
  statusText?: string;
  reason?: string;
  extraDetails?: PaymentStatusDetail[];
  action?: PaymentStatusAction;
  loadingText?: string;
  redirect?: PaymentRedirectConfig;
  logoUrl?: string | null;
  embedded?: boolean;
  locale?: CheckoutLocale;
};

const VARIANT_COPY: Record<
  Exclude<PaymentStatusVariant, 'loading'>,
  { badge: string; title: string; message: string; Icon: React.ComponentType<{ size?: number }> }
> = {
  success: {
    badge: 'Payment successful',
    title: 'Payment successful',
    message: 'Your payment has been completed successfully.',
    Icon: BsCheckLg,
  },
  error: {
    badge: 'Payment failed',
    title: 'Payment failed',
    message: "We couldn't complete your payment. Please review the transaction details below.",
    Icon: BsXLg,
  },
  pending: {
    badge: 'Payment processing',
    title: 'Your payment is being processed',
    message: "We're securely processing your payment. This usually takes a few moments.",
    Icon: BsClock,
  },
};

function isExternalHttpUrl(value: string) {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

const BRAND_LOGOS: Record<Exclude<PaymentStatusVariant, 'loading'>, string> = {
  success: logoSuccess,
  error: logoError,
  pending: logoPending,
};

function BrandWordmark({ variant }: { variant: Exclude<PaymentStatusVariant, 'loading'> }) {
  return (
    <img
      src={BRAND_LOGOS[variant]}
      alt="money eu"
      className="psc-logo"
    />
  );
}

const GLOW_PARTICLES = [
  { left: '6%', top: '12%', size: 3, duration: 26, delay: 0, path: 'a' },
  { left: '18%', top: '68%', size: 5, duration: 32, delay: 2, path: 'b' },
  { left: '28%', top: '22%', size: 2, duration: 22, delay: 5, path: 'c' },
  { left: '41%', top: '84%', size: 4, duration: 29, delay: 1, path: 'd' },
  { left: '52%', top: '8%', size: 3, duration: 34, delay: 7, path: 'a' },
  { left: '63%', top: '46%', size: 6, duration: 27, delay: 3, path: 'b' },
  { left: '74%', top: '16%', size: 2, duration: 21, delay: 8, path: 'c' },
  { left: '82%', top: '72%', size: 4, duration: 36, delay: 4, path: 'd' },
  { left: '91%', top: '28%', size: 3, duration: 24, delay: 6, path: 'a' },
  { left: '11%', top: '42%', size: 2, duration: 30, delay: 9, path: 'b' },
  { left: '35%', top: '58%', size: 5, duration: 38, delay: 1.5, path: 'c' },
  { left: '47%', top: '34%', size: 3, duration: 23, delay: 11, path: 'd' },
  { left: '58%', top: '78%', size: 2, duration: 28, delay: 4.5, path: 'a' },
  { left: '69%', top: '62%', size: 4, duration: 33, delay: 0.8, path: 'b' },
  { left: '88%', top: '52%', size: 3, duration: 25, delay: 6.5, path: 'c' },
  { left: '4%', top: '86%', size: 5, duration: 31, delay: 2.2, path: 'd' },
  { left: '22%', top: '6%', size: 2, duration: 19, delay: 10, path: 'a' },
  { left: '78%', top: '90%', size: 3, duration: 35, delay: 3.4, path: 'b' },
  { left: '14%', top: '54%', size: 4, duration: 27, delay: 8.5, path: 'c' },
  { left: '96%', top: '8%', size: 2, duration: 22, delay: 5.5, path: 'd' },
] as const;

function CopyIdButton({ value, copy }: { value: string; copy: CheckoutCopy }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  return (
    <button type="button" className="psc-copy" onClick={handleCopy}>
      <BsClipboard size={12} />
      {copied ? copy.copiedShort : copy.copy}
    </button>
  );
}

const PaymentStatusCard: React.FC<PaymentStatusCardProps> = ({
  variant,
  title,
  message,
  transactionId,
  amount,
  statusText,
  reason,
  extraDetails = [],
  action,
  loadingText,
  redirect,
  logoUrl,
  embedded = false,
  locale = 'en',
}) => {
  const i18n = getCheckoutCopy(locale);
  const actionOnClickRef = useRef(action?.onClick);
  actionOnClickRef.current = action?.onClick;
  const [spinOnce, setSpinOnce] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  const redirectSeconds = Math.max(0, Math.floor(Number(redirect?.timerSeconds) || 0));
  const redirectTarget = redirect?.redirectUrl?.trim() || '';
  const canRedirect = !embedded && redirectSeconds > 0 && isExternalHttpUrl(redirectTarget);
  const [secondsLeft, setSecondsLeft] = useState(redirectSeconds);

  const visualVariant: Exclude<PaymentStatusVariant, 'loading'> =
    variant === 'loading' ? 'pending' : variant;
  const copy = VARIANT_COPY[visualVariant];
  const StatusIcon = copy.Icon;
  const merchantLogo = logoUrl?.trim() || '';
  const showMerchantLogo =
    !logoFailed &&
    merchantLogo.length > 0 &&
    isExternalHttpUrl(merchantLogo);
  const resolvedLoadingText = loadingText ?? i18n.loadingDetails;
  const variantBadge =
    visualVariant === 'success'
      ? i18n.statusSuccessBadge
      : visualVariant === 'error'
        ? i18n.statusErrorBadge
        : i18n.statusPendingBadge;
  const variantTitle =
    visualVariant === 'success'
      ? i18n.statusSuccessTitle
      : visualVariant === 'error'
        ? i18n.statusErrorTitle
        : i18n.statusPendingTitle;
  const variantMessage =
    visualVariant === 'success'
      ? i18n.statusSuccessMessage
      : visualVariant === 'error'
        ? i18n.statusErrorMessage
        : i18n.statusPendingMessage;

  useEffect(() => {
    setLogoFailed(false);
  }, [merchantLogo]);

  useEffect(() => {
    if (embedded) return;
    document.documentElement.classList.add('psc-page');
    return () => document.documentElement.classList.remove('psc-page');
  }, [embedded]);

  // One effect owns the whole countdown lifecycle for a given (canRedirect, redirectSeconds,
  // redirectTarget) combination: it resets secondsLeft immediately, then ticks it down on an
  // interval and navigates only once the interval itself reaches zero. Splitting "reset" and
  // "check-and-redirect" into two separate effects raced: canRedirect can flip true (via a fresh
  // `redirect` prop) on the same render where secondsLeft is still a stale value from a previous
  // mount (e.g. an earlier render with no `redirect` prop at all, where secondsLeft started at 0)
  // — the redirect effect would see canRedirect=true and the stale secondsLeft=0 together and
  // navigate instantly, before the reset effect ever got a chance to apply the real countdown.
  useEffect(() => {
    if (!canRedirect) {
      setSecondsLeft(redirectSeconds);
      return;
    }

    setSecondsLeft(redirectSeconds);

    const intervalId = window.setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          window.clearInterval(intervalId);
          window.location.assign(redirectTarget);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [canRedirect, redirectSeconds, redirectTarget]);

  const handleActionClick = () => {
    if (action?.disabled) return;
    setSpinOnce(true);
    action?.onClick();
  };

  useEffect(() => {
    if (!action?.autoTriggerAfterMs || action.disabled) return;

    const timer = window.setTimeout(() => {
      actionOnClickRef.current?.();
    }, action.autoTriggerAfterMs);

    return () => window.clearTimeout(timer);
  }, [action?.autoTriggerAfterMs, action?.disabled]);

  const heading = title ?? variantTitle;
  const subtext = message ?? (variant === 'loading' ? resolvedLoadingText : variantMessage);
  const hasOverview = Boolean(transactionId || amount || statusText || reason || extraDetails.length);

  return (
    <div className={`psc psc--${visualVariant}${embedded ? ' psc--embedded' : ''}`} lang={locale} dir={checkoutDir(locale)}>
      <div className="psc-atmosphere" aria-hidden="true" />
      <div className="psc-particles" aria-hidden="true">
        {GLOW_PARTICLES.map((particle, index) => (
          <span
            key={index}
            className={`psc-particle psc-particle--${particle.path}`}
            style={{
              left: particle.left,
              top: particle.top,
              width: particle.size,
              height: particle.size,
              ['--psc-dur' as string]: `${particle.duration}s`,
              ['--psc-delay' as string]: `${particle.delay}s`,
            }}
          />
        ))}
      </div>
      <header className="psc-header">
        {showMerchantLogo ? (
          <img
            src={merchantLogo}
            alt="Merchant logo"
            className="psc-merchant-logo"
            onError={() => setLogoFailed(true)}
          />
        ) : (
          <BrandWordmark variant={visualVariant} />
        )}
      </header>

      <main className="psc-main">
        <div className="psc-orb" aria-hidden="true">
          <span className="psc-orb-ring psc-orb-ring--a" />
          <span className="psc-orb-ring psc-orb-ring--b" />
          <span className="psc-orb-ring psc-orb-ring--c" />
          <span className="psc-spark psc-spark--1" />
          <span className="psc-spark psc-spark--2" />
          <span className="psc-spark psc-spark--3" />
          <span className="psc-spark psc-spark--4" />
          <div className="psc-orb-core">
            <StatusIcon size={38} />
          </div>
        </div>

        <div className="psc-badge-row">
          <span className="psc-badge">{variantBadge}</span>
        </div>

        <h1 className="psc-title">{heading}</h1>
        {subtext ? <p className="psc-message">{subtext}</p> : null}

        {canRedirect && (
          <p className="psc-redirect" aria-live="polite">
            {secondsLeft === 0 ? i18n.redirectWait : i18n.redirectIn(secondsLeft)}
          </p>
        )}

        {hasOverview && (
          <section className="psc-card" aria-label={i18n.transactionOverview}>
            <div className="psc-card-head">
              <div>
                <p className="psc-card-kicker">{i18n.transactionOverview}</p>
                <h2 className="psc-card-title">{i18n.paymentDetails}</h2>
              </div>
              <div className="psc-card-actions">
                {action && visualVariant === 'pending' && (
                  <button
                    type="button"
                    className="psc-refresh"
                    onClick={handleActionClick}
                    disabled={action.disabled}
                    aria-label={action.hint ?? action.label}
                    title={action.hint ?? action.label}
                  >
                    <BsArrowRepeat
                      size={14}
                      className={action.disabled ? 'spin-loop' : spinOnce ? 'spin-once' : ''}
                      onAnimationEnd={() => setSpinOnce(false)}
                    />
                  </button>
                )}
                <span className="psc-secure">
                  {visualVariant === 'pending' ? <BsShieldFill /> : <BsLockFill />}
                  {i18n.secure}
                </span>
              </div>
            </div>

            <div className="psc-grid">
              <div className="psc-cell" style={{ padding: '16px 16px 16px 0' }}>
                <span className="psc-label">{i18n.transactionId}</span>
                <div className="psc-value">{transactionId || '—'}</div>
                {transactionId ? <CopyIdButton value={transactionId} copy={i18n} /> : null}
              </div>
              <div className="psc-cell">
                <span className="psc-label">{i18n.amount}</span>
                <div className="psc-value">{amount || '—'}</div>
              </div>
              <div className="psc-cell">
                <span className="psc-label">{i18n.status}</span>
                <div className="psc-status">
                  <span className="psc-status-dot" />
                  {statusText || visualVariant.toUpperCase()}
                </div>
              </div>
            </div>

            {reason ? (
              <div className="psc-reason">
                <span className="psc-label">{i18n.reason}</span>
                <span className="psc-reason-value">
                  {visualVariant === 'success' ? (
                    <BsCheckLg />
                  ) : visualVariant === 'error' ? (
                    <BsXCircle />
                  ) : (
                    <BsClock />
                  )}
                  {reason}
                </span>
              </div>
            ) : null}

            {extraDetails.length > 0 ? (
              <div className="psc-extra">
                {extraDetails.map((detail, index) => (
                  <div className="psc-extra-item" key={`${detail.label}-${index}`}>
                    <span className="psc-label">{detail.label}</span>
                    <div className="psc-value">{detail.value}</div>
                  </div>
                ))}
              </div>
            ) : null}
          </section>
        )}
      </main>
    </div>
  );
};

export default PaymentStatusCard;
